'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { toast } from 'sonner';
import RegenerateModal from './RegenerateModal';
import StorageWidget from './StorageWidget';
import { useInboxTheme } from '@/contexts/InboxThemeContext';

const TEMP_EMAIL = 'xk7-mercer@mailnova.io';
const EXPIRY_SECONDS_INITIAL = 3600 * 2 + 1447;
const DOMAINS = ['mailnova.io', 'dropbox.email', 'fastinbox.net'];

type Folder = 'inbox' | 'starred' | 'trash';

interface InboxSidebarProps {
  open: boolean;
  activeFolder: Folder;
  onFolderChange: (f: Folder) => void;
}

const folders: { id: Folder; label: string; count: number }[] = [
  { id: 'inbox', label: 'Inbox', count: 3 },
  { id: 'starred', label: 'Starred', count: 2 },
  { id: 'trash', label: 'Trash', count: 1 },
];

function FolderIcon({ id }: { id: Folder }) {
  if (id === 'inbox') return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="22 12 16 12 14 15 10 15 8 12 2 12" />
      <path d="M5.45 5.11L2 12v6a2 2 0 002 2h16a2 2 0 002-2v-6l-3.45-6.89A2 2 0 0016.76 4H7.24a2 2 0 00-1.79 1.11z" />
    </svg>
  );
  if (id === 'starred') return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
    </svg>
  );
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a1 1 0 011-1h4a1 1 0 011 1v2" />
    </svg>
  );
}

export default function InboxSidebar({ open, activeFolder, onFolderChange }: InboxSidebarProps) {
  const [copied, setCopied] = useState(false);
  const [selectedDomain, setSelectedDomain] = useState(DOMAINS[0]);
  const [showDomainPicker, setShowDomainPicker] = useState(false);
  const [showRegenModal, setShowRegenModal] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(EXPIRY_SECONDS_INITIAL);
  const { themeConfig } = useInboxTheme();

  useEffect(() => {
    const timer = setInterval(() => {
      setSecondsLeft((s) => Math.max(0, s - 1));
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const formatExpiry = (secs: number) => {
    const h = Math.floor(secs / 3600);
    const m = Math.floor((secs % 3600) / 60);
    const s = secs % 60;
    if (h > 0) return `${h}h ${m}m`;
    if (m > 0) return `${m}m ${s}s`;
    return `${s}s`;
  };

  const expiryWarning = secondsLeft < 600;
  const expiryDanger = secondsLeft < 120;

  const handleCopy = () => {
    navigator.clipboard.writeText(TEMP_EMAIL).then(() => {
      setCopied(true);
      toast.success('Address copied to clipboard');
      setTimeout(() => setCopied(false), 2500);
    });
  };

  if (!open) {
    return (
      <div className="w-16 flex-shrink-0 inbox-glass-sidebar flex flex-col items-center py-4 gap-4 relative z-10">
        <div
          className="w-8 h-8 rounded-lg flex items-center justify-center font-bold text-sm border"
          style={{ background: `${themeConfig.color}25`, borderColor: `${themeConfig.color}40`, color: themeConfig.color }}
        >
          A
        </div>
        <div className="flex flex-col gap-1 mt-2">
          {folders.map((f) => (
            <button
              key={`nav-icon-${f.id}`}
              onClick={() => onFolderChange(f.id)}
              className={`relative p-2.5 rounded-lg transition-all duration-150 ${
                activeFolder === f.id
                  ? 'bg-white/15 inbox-text-primary' :'inbox-text-muted hover:inbox-text-foreground hover:bg-white/10'
              }`}
              title={f.label}
            >
              <FolderIcon id={f.id} />
              {f.count > 0 && (
                <span className="absolute top-1 right-1 w-2 h-2 rounded-full" style={{ background: themeConfig.color }} />
              )}
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="w-72 flex-shrink-0 inbox-glass-sidebar flex flex-col overflow-hidden transition-all duration-300 relative z-10">
        {/* Address card */}
        <div className="p-4 border-b border-white/10">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold inbox-text-muted uppercase tracking-wider">Active address</span>
            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
              expiryDanger
                ? 'bg-red-500/20 text-red-400 border border-red-500/30'
                : expiryWarning
                ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30' :'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
            }`}>
              {secondsLeft === 0 ? 'Expired' : `Expires in ${formatExpiry(secondsLeft)}`}
            </span>
          </div>

          <div
            className={`flex items-center gap-2 p-2.5 rounded-lg inbox-glass cursor-pointer transition-all duration-150 hover:bg-white/15 ${
              expiryDanger ? 'animate-countdown-pulse' : ''
            }`}
            onClick={handleCopy}
            title="Click to copy"
          >
            <span className="flex-1 text-sm font-mono font-medium inbox-text-foreground truncate">{TEMP_EMAIL}</span>
            {copied ? (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#10B981" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            ) : (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="inbox-text-muted">
                <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
              </svg>
            )}
          </div>

          {/* Domain picker */}
          <div className="mt-2 relative">
            <button
              onClick={() => setShowDomainPicker(!showDomainPicker)}
              className="w-full flex items-center justify-between px-2.5 py-1.5 rounded-lg inbox-glass text-xs inbox-text-muted hover:inbox-text-foreground hover:bg-white/15 transition-all duration-150"
            >
              <span>@{selectedDomain}</span>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`transition-transform duration-150 ${showDomainPicker ? 'rotate-180' : ''}`}>
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button>
            {showDomainPicker && (
              <div className="absolute top-full left-0 right-0 mt-1 inbox-glass-intense rounded-xl shadow-elevated z-10 overflow-hidden animate-slide-up">
                {DOMAINS.map((d) => (
                  <button
                    key={`domain-opt-${d}`}
                    onClick={() => { setSelectedDomain(d); setShowDomainPicker(false); toast.success(`Domain switched to @${d}`); }}
                    className={`w-full text-left px-3 py-2 text-xs font-mono hover:bg-white/15 transition-colors ${selectedDomain === d ? 'inbox-text-primary font-semibold' : 'inbox-text-foreground'}`}
                  >
                    @{d}
                  </button>
                ))}
                <Link
                  href="/domains"
                  className="w-full flex items-center gap-1.5 px-3 py-2 text-xs font-semibold hover:bg-white/15 transition-colors border-t border-white/10 inbox-text-primary"
                >
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="12" y1="5" x2="12" y2="19" />
                    <line x1="5" y1="12" x2="19" y2="12" />
                  </svg>
                  Add custom domain
                </Link>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="mt-3 flex gap-2">
            <button
              onClick={() => setShowRegenModal(true)}
              className="flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-lg inbox-glass text-xs font-medium inbox-text-muted hover:inbox-text-foreground hover:bg-white/15 transition-all duration-150"
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="23 4 23 10 17 10" />
                <path d="M20.49 15a9 9 0 11-2.12-9.36L23 10" />
              </svg>
              Regenerate
            </button>
            <Link
              href="/upgrade-pricing-screen"
              className="flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-lg text-xs font-semibold transition-all duration-150 border"
              style={{
                background: `${themeConfig.color}20`,
                borderColor: `${themeConfig.color}40`,
                color: themeConfig.color,
              }}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              </svg>
              Make permanent
            </Link>
          </div>
        </div>

        {/* Folders */}
        <nav className="px-3 py-3">
          <p className="text-xs font-semibold inbox-text-muted uppercase tracking-wider px-2 mb-2">Folders</p>
          {folders.map((f) => (
            <button
              key={`folder-nav-${f.id}`}
              onClick={() => onFolderChange(f.id)}
              className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-150 mb-0.5 ${
                activeFolder === f.id
                  ? 'bg-white/15 inbox-text-primary border border-white/15' :'inbox-text-secondary hover:inbox-text-foreground hover:bg-white/10'
              }`}
            >
              <FolderIcon id={f.id} />
              <span className="flex-1 text-left">{f.label}</span>
              {f.count > 0 && (
                <span
                  className={`text-xs font-semibold px-1.5 py-0.5 rounded-full ${
                    activeFolder === f.id ? 'bg-white/20 inbox-text-primary' : 'bg-white/10 inbox-text-muted'
                  }`}
                >
                  {f.count}
                </span>
              )}
            </button>
          ))}
        </nav>

        {/* Quick links */}
        <div className="px-3 py-2 border-t border-white/10">
          <p className="text-xs font-semibold inbox-text-muted uppercase tracking-wider px-2 mb-2">Tools</p>
          <Link href="/secure-paste"
            className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium inbox-text-secondary hover:inbox-text-foreground hover:bg-white/10 transition-all duration-150 mb-0.5">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/>
            </svg>
            SecurePaste
          </Link>
          <Link href="/domains"
            className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium inbox-text-secondary hover:inbox-text-foreground hover:bg-white/10 transition-all duration-150">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/>
              <path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z"/>
            </svg>
            Custom Domains
          </Link>
        </div>

        {/* Storage widget */}
        <div className="mt-auto p-4 border-t border-white/10">
          <StorageWidget />
          <Link
            href="/upgrade-pricing-screen"
            className="mt-3 w-full flex items-center justify-center gap-2 py-2 rounded-lg text-xs font-semibold transition-all duration-150 inbox-btn-primary"
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
              <polyline points="17 6 23 6 23 12" />
            </svg>
            Upgrade to Pro
          </Link>
        </div>
      </div>

      {showRegenModal && (
        <RegenerateModal
          currentEmail={TEMP_EMAIL}
          onConfirm={() => {
            setShowRegenModal(false);
            toast.success('New address generated — xp9-swift@mailnova.io');
          }}
          onCancel={() => setShowRegenModal(false)}
        />
      )}
    </>
  );
}